import { NumberSize, ResizeDirection } from "re-resizable";
import { useMemo, useState } from "react";
import useLocalStorage from "utilities/hooks/use-local-storage";
import {
    ResizablePanelDimensions,
    useResizeStrategy,
} from "./use-resize-strategy";

interface UseResizeContainerOptions {
    id: string;
    isHorizontal?: boolean;
    startingResizableDimension: number;
}

interface UseResizeContainer {
    defaultResizableDimension: number;
    handleResizeStart: () => void;
    handleResizeStop: (
        event: MouseEvent | TouchEvent,
        direction: ResizeDirection,
        elementRef: HTMLElement,
        delta: NumberSize
    ) => void;
    isResizing: boolean;
    size: ResizablePanelDimensions;
}

const useResizeContainer = ({
    id,
    startingResizableDimension,
    isHorizontal = true,
}: UseResizeContainerOptions): UseResizeContainer => {
    const {
        getValue: defaultResizableDimension,
        setValue: setDefaultResizableDimension,
    } = useLocalStorage(id, startingResizableDimension);
    const [isResizing, setIsResizing] = useState(false);
    const resizeStrategy = useResizeStrategy(isHorizontal);

    const handleResizeStart = () => {
        setIsResizing(true);
    };

    const handleResizeStop = (
        event: MouseEvent | TouchEvent,
        direction: ResizeDirection,
        elementRef: HTMLElement,
        delta: NumberSize
    ) => {
        const newDimension = resizeStrategy.getNewDimension(elementRef);
        setDefaultResizableDimension(newDimension);
        setIsResizing(false);
    };

    const size = useMemo(() => {
        return resizeStrategy.size(
            defaultResizableDimension(),
            startingResizableDimension
        );
    }, [defaultResizableDimension, resizeStrategy, startingResizableDimension]);

    return {
        handleResizeStop,
        defaultResizableDimension:
            defaultResizableDimension() ?? startingResizableDimension,
        size,
        handleResizeStart,
        isResizing,
    };
};

export default useResizeContainer;
